import {
  Autocomplete,
  InputAdornment,
  Skeleton,
  TextField,
} from "@mui/material";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { times } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "use-intl";

import { rudderAnalytics, TripTrackingEvents } from "@/app/lib/rudderstack";
import { ErrorPanel } from "@/app/ui/ErrorPanel";
import { IconAirplaneCrossed } from "@/components/Icons";
import {
  TripCard,
  TripCardSkeleton,
} from "@/features/trips/components/TripCard";
import { tripsQueryOptions } from "@/features/trips/hooks/useTrips";

export function TripList() {
  const t = useTranslations();
  const navigate = useNavigate();
  const [countryFilter, setCountryFilter] = useState("");

  const { data, isLoading, isError } = useQuery(tripsQueryOptions());

  const countries = useMemo(() => {
    const allCountries = data?.flatMap((trip) => trip.countriesNames) || [];
    return Array.from(new Set(allCountries)).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const isEmpty = !isLoading && (!data || data.length === 0);

  useEffect(() => {
    if (data) {
      rudderAnalytics.track(TripTrackingEvents.USER_VIEWED_TRIP_LIST);
    }
  }, [data]);

  return (
    <div>
      <div>
        {isError ? (
          <ErrorPanel
            icon={IconAirplaneCrossed}
            primaryText={t("trips.errorState")}
            secondaryText={t("trips.errorStateDesc")}
          />
        ) : isEmpty ? (
          <div className="mt-12 flex grow flex-col items-center justify-between gap-8">
            <ErrorPanel
              icon={IconAirplaneCrossed}
              primaryText={t("trips.emptyState")}
              secondaryText={t("trips.emptyStateDesc")}
            />
          </div>
        ) : (
          <div className="w-full">
            <div className="pb-4">
              {isLoading ? (
                <Skeleton width="100%" height={57} variant="rounded" />
              ) : (
                <Autocomplete<string>
                  options={countries}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      // Autocomplete uses deprecated InputProps, cannot use slots because it breaks Autocomplete default styling
                      // eslint-disable-next-line @typescript-eslint/no-deprecated
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <MagnifyingGlassIcon
                              size={20}
                              color="var(--color-text-disabled-tertiary)"
                            />
                          </InputAdornment>
                        ),
                      }}
                      placeholder={t("trips.searchByCountry")}
                      sx={{
                        ".MuiInputAdornment-root": {
                          transform: "none",
                        },
                        ".MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                          marginTop: "16px",
                          marginBottom: "16px",
                        },
                        ".MuiOutlinedInput-notchedOutline": {
                          top: 0,
                        },
                      }}
                    />
                  )}
                  value={countryFilter}
                  onChange={(event, value) => setCountryFilter(value || "")}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  forcePopupIcon={false}
                />
              )}
            </div>

            <ul className="space-y-3">
              {isLoading ? (
                <>
                  {times(3, (index) => (
                    <li key={index}>
                      <TripCardSkeleton />
                    </li>
                  ))}
                </>
              ) : (
                data
                  ?.filter(
                    (trip) =>
                      !countryFilter ||
                      trip.countriesNames.includes(countryFilter),
                  )
                  .map((trip) => (
                    <li key={trip.id}>
                      <TripCard
                        trip={trip}
                        onClick={() => {
                          navigate({
                            to: "/trips/$tripId",
                            params: { tripId: trip.id },
                          });
                        }}
                      />
                    </li>
                  ))
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
